import { ChangeDetectionStrategy, Component, computed, input, output } from '@angular/core';
import { TranslatePipe } from '@ngx-translate/core';
import { FilterDropdownComponent } from '../../../components/filter-dropdown/filter-dropdown.component';
import { ResetFilterButtonComponent } from '../../../components/reset-filter-button/reset-filter-button.component';
import { SavedViewOption, SavedViewsMenuComponent } from '../../../shared/saved-views/saved-views-menu.component';

/**
 * Filter-Optionen fuer die Testschritte-Leiste. Struktur ist bewusst identisch zu
 * {@link FilterDropdownOption}, damit die Option-Arrays direkt an das Dropdown
 * durchgereicht werden koennen (Design-Referenz page-testschritte.jsx:227-229,
 * Bundle v2026-08-25-d1654a822bf).
 */
export interface FilterOption {
  value: string;
  label: string;
}

export type TestStepFilterKey = 'category' | 'status' | 'author';

export interface FilterChange {
  key: TestStepFilterKey;
  value: string | null;
}

@Component({
  selector: 'app-test-step-filter-bar',
  standalone: true,
  imports: [TranslatePipe, FilterDropdownComponent, ResetFilterButtonComponent, SavedViewsMenuComponent],
  templateUrl: './test-step-filter-bar.component.html',
  styleUrl: './test-step-filter-bar.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class TestStepFilterBarComponent {
  readonly categoryOptions = input<FilterOption[]>([]);
  readonly statusOptions = input<FilterOption[]>([]);
  readonly authorOptions = input<FilterOption[]>([]);

  readonly selectedCategory = input<string[]>([]);
  readonly selectedStatus = input<string[]>([]);
  readonly selectedAuthor = input<string[]>([]);

  readonly savedViews = input<SavedViewOption[]>([]);
  readonly activeSavedView = input<string | null>(null);
  readonly projectId = input.required<string>();

  readonly filterChange = output<FilterChange>();
  readonly resetFilters = output<void>();
  readonly savedViewSelected = output<string>();
  readonly savedViewSaved = output<string>();
  readonly savedViewDeleted = output<string>();
  readonly savedViewDeleteFailed = output<string>();

  readonly categoryValue = computed(() => this.selectedCategory()[0] ?? '');
  readonly statusValue = computed(() => this.selectedStatus()[0] ?? '');
  readonly authorValue = computed(() => this.selectedAuthor()[0] ?? '');

  readonly hasFilters = computed(
    () => this.selectedCategory().length > 0 || this.selectedStatus().length > 0 || this.selectedAuthor().length > 0,
  );

  onChange(key: TestStepFilterKey, raw: string): void {
    this.filterChange.emit({ key, value: raw || null });
  }

  onReset(): void {
    this.resetFilters.emit();
  }

  onSavedViewSelected(name: string): void {
    this.savedViewSelected.emit(name);
  }

  onSavedViewSaved(name: string): void {
    this.savedViewSaved.emit(name);
  }

  onSavedViewDeleted(name: string): void {
    this.savedViewDeleted.emit(name);
  }

  onSavedViewDeleteFailed(name: string): void {
    this.savedViewDeleteFailed.emit(name);
  }
}
